import React, { useEffect } from 'react';
import { useAdminStore } from '@/store/adminStore';
import { useI18n } from '@/components/I18nProvider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Building2,
  Users,
  MessageCircle,
  CreditCard,
  Ban,
  TrendingUp,
  Clock,
  AlertTriangle,
  Activity,
  DollarSign,
  Wifi,
  UserCheck,
  BarChart3,
  ArrowUpRight,
  FileText,
  Percent,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  Legend,
} from 'recharts';

const formatCurrency = (val) => {
  const formatted = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);
  return `${formatted} TL`;
};

const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <Card className="transition-all duration-200 hover:shadow-md border-0 shadow-sm" data-testid={`admin-stat-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-4 sm:p-5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs sm:text-sm font-medium text-muted-foreground truncate">{title}</p>
          <p className="mt-1 font-heading text-xl sm:text-2xl font-bold">{value}</p>
          {subtitle && <p className="mt-0.5 text-[11px] text-muted-foreground">{subtitle}</p>}
        </div>
        <div className={`rounded-lg p-2.5 shrink-0 ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const PKG_COLORS = ['#f59e0b', '#3b82f6', '#8b5cf6', '#10b981', '#ef4444', '#06b6d4'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-card p-2.5 shadow-lg text-sm">
      <p className="font-medium">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="text-xs mt-0.5">{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

export default function AdminDashboardPage() {
  const { dashboardStats, isLoading, fetchDashboard } = useAdminStore();
  const { t } = useI18n();

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (isLoading && !dashboardStats) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="admin-dashboard-page">
        <div><h1 className="font-heading text-2xl sm:text-3xl font-bold">Super Admin Panel</h1></div>
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => <Card key={i}><CardContent className="p-5"><Skeleton className="h-16 w-full" /></CardContent></Card>)}
        </div>
      </div>
    );
  }

  const s = dashboardStats || {};
  const pkgDist = s.package_distribution || [];
  const growthData = s.growth_trend || [];
  const recentActs = s.recent_activities || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-dashboard-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">Super Admin Panel</h1>
        <p className="mt-1 text-sm text-muted-foreground">Platform genelinde istatistikler ve analizler</p>
      </div>

      {/* Revenue Cards - Top priority */}
      <div className="grid gap-3 md:grid-cols-2">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-950/30 dark:to-emerald-900/20">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-emerald-700/80 dark:text-emerald-400/80">{t('subscriptionRevenue')}</p>
                <p className="mt-1 font-heading text-2xl sm:text-3xl font-bold text-emerald-700 dark:text-emerald-400" data-testid="subscription-revenue">{formatCurrency(s.subscription_revenue || 0)}</p>
                <p className="mt-1 text-xs text-emerald-600/70 dark:text-emerald-500/70">{s.active_companies || 0} {t('fromActiveSubscriptions')}</p>
              </div>
              <div className="rounded-xl p-3 bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
                <TrendingUp className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/20">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-primary/80 dark:text-primary-foreground/80">{t('monthlyRevenue')}</p>
                <p className="mt-1 font-heading text-2xl sm:text-3xl font-bold text-primary dark:text-primary-foreground" data-testid="monthly-revenue">{formatCurrency(s.monthly_revenue || 0)}</p>
                <p className="mt-1 text-xs text-primary/70 dark:text-primary-foreground/70">{t('newSubscriptionsThisMonth')}</p>
              </div>
              <div className="rounded-xl p-3 bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-foreground">
                <DollarSign className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main KPI Grid */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatCard title={t('totalCompanies')} value={s.total_companies || 0} icon={Building2} color="bg-primary/10 text-primary dark:bg-primary/20" subtitle={`${s.active_companies || 0} ${t('activeSubscription')}`} />
        <StatCard title={t('trial')} value={s.trial_companies || 0} icon={Clock} color="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400" subtitle={t('activeTrial')} />
        <StatCard title={t('expired')} value={s.expired_trials || 0} icon={AlertTriangle} color="bg-destructive/10 text-destructive" subtitle={t('subscriptionNeeded')} />
        <StatCard title={t('pendingTransfer')} value={s.pending_payment_companies || 0} icon={Clock} color="bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400" subtitle={t('awaitingApproval')} />
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatCard title={t('totalUsers')} value={s.total_users || 0} icon={Users} color="bg-primary/10 text-primary dark:bg-primary/20" subtitle={`${t('avgUsers')} ${s.avg_users_per_company || 0} / ${t('perCompany').split(' ')[0]}`} />
        <StatCard title={t('totalCustomers')} value={s.total_cari || 0} icon={FileText} color="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400" />
        <StatCard title={t('totalPayments')} value={s.total_payments || 0} icon={CreditCard} color="bg-primary/10 text-primary dark:bg-primary/20" />
        <StatCard title={t('whatsappConnected')} value={s.whatsapp_connected_count || 0} icon={Wifi} color="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400" />
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatCard title={t('messagesToday')} value={s.total_messages_today || 0} icon={MessageCircle} color="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400" />
        <StatCard title={t('messagesMonth')} value={s.total_messages_month || 0} icon={Activity} color="bg-primary/10 text-primary dark:bg-primary/20" />
        <StatCard title={t('conversionRate')} value={`%${s.conversion_rate || 0}`} icon={Percent} color="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400" subtitle={t('trialToSub')} />
        <StatCard title={t('avgUsers')} value={s.avg_users_per_company || 0} icon={UserCheck} color="bg-primary/10 text-primary dark:bg-primary/20" subtitle={t('perCompany')} />
      </div>

      {/* Charts Row */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Package Distribution */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 className="h-4 w-4 text-primary" />
              Paket Dagilimi
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            {pkgDist.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={pkgDist.filter(p => p.count > 0)}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="count"
                    nameKey="name"
                  >
                    {pkgDist.filter(p => p.count > 0).map((entry, idx) => (
                      <Cell key={idx} fill={entry.color || PKG_COLORS[idx % PKG_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground">Veri yok</div>
            )}
            {/* Package counts below chart */}
            <div className="flex flex-wrap gap-2 mt-2">
              {pkgDist.map((p, i) => (
                <Badge key={i} variant="outline" className="text-xs" style={{ borderColor: p.color || PKG_COLORS[i % PKG_COLORS.length] }}>
                  {p.name}: {p.count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Growth Trend */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <ArrowUpRight className="h-4 w-4 text-primary" />
              Kayit Trendi (Son 30 Gun)
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            {growthData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={growthData}>
                  <defs>
                    <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(25, 95%, 53%)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(25, 95%, 53%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="count" name="Yeni Firma" stroke="hsl(25, 95%, 53%)" fillOpacity={1} fill="url(#growthGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-sm text-muted-foreground">Veri yok</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activities */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-4 w-4 text-primary" />
            Son Aktiviteler
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentActs.length > 0 ? (
            <div className="space-y-0">
              {recentActs.map((act, i) => (
                <div key={act.id + i} className="flex items-center gap-3 py-2.5 border-b last:border-0" data-testid={`recent-activity-${i}`}>
                  <div className={`h-2 w-2 rounded-full shrink-0 ${act.action === 'Abonelik aktif' ? 'bg-green-500' : 'bg-amber-500'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{act.company_name}</span>
                      <Badge variant={act.action === 'Abonelik aktif' ? 'default' : 'outline'} className="text-[10px] shrink-0">
                        {act.action}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{act.detail}</p>
                  </div>
                  <span className="text-[10px] text-muted-foreground shrink-0 hidden sm:block">
                    {act.date ? new Date(act.date).toLocaleDateString('tr-TR') : '-'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">Henuz aktivite yok</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
